import React, { useState, useEffect } from 'react';
import { Box, Typography, Modal, TextField, Button, Grid, IconButton, Autocomplete, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { HousekeepingService } from '../api/services/housekeepingService';
import { RoomService } from '../api/services/roomService';
import { StaffService } from '../api/services/staffService';
import { ReservationService } from '../api/services/reservationService';
import { useSelector } from 'react-redux';
import { toast } from '../utils/toast';

export default function LostFoundFormModal({ open, onClose, onSuccess }) {
    const { user } = useSelector(state => state.auth);
    const tenantId = user?.tenantId || '1';

    const [formData, setFormData] = useState({
        description: '',
        roomId: '',
        dateFound: '',
        foundByStaffId: '',
        storageLocation: '',
        reservationId: ''
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoadingData, setIsLoadingData] = useState(false);
    const [rooms, setRooms] = useState([]);
    const [staff, setStaff] = useState([]);
    const [reservations, setReservations] = useState([]);

    useEffect(() => {
        if (open) {
            fetchDependencies();
            // Default date to today
            setFormData(prev => ({ ...prev, dateFound: new Date().toISOString().split('T')[0] }));
        } else {
            // Clean out local state hooks
            setFormData({
                description: '',
                roomId: '',
                dateFound: '',
                foundByStaffId: '',
                storageLocation: '',
                reservationId: ''
            });
        }
    }, [open]);

    const fetchDependencies = async () => {
        setIsLoadingData(true);
        try {
            const [roomsRes, staffRes, resRes] = await Promise.all([
                RoomService.getAll({ noPagination: true }, tenantId).catch(err => {
                    console.warn('RoomService.getAll failed in LostFoundFormModal:', err);
                    return [];
                }),
                StaffService.getStaff({ noPagination: true }).catch(err => {
                    console.warn('StaffService.getStaff failed in LostFoundFormModal:', err);
                    return [];
                }),
                ReservationService.getAll({ noPagination: true, status: 'All' }, tenantId).catch(err => {
                    console.warn('ReservationService.getAll failed in LostFoundFormModal:', err);
                    return [];
                })
            ]);
            
            const roomList = Array.isArray(roomsRes)
                ? roomsRes
                : (Array.isArray(roomsRes?.data)
                    ? roomsRes.data
                    : (Array.isArray(roomsRes?.rooms) ? roomsRes.rooms : []));
            setRooms(roomList);

            const staffList = Array.isArray(staffRes) ? staffRes : (staffRes?.data || []);
            setStaff(staffList.filter(s => (s.status || 'Active').toLowerCase() === 'active'));
            setReservations(Array.isArray(resRes) ? resRes : (resRes?.data || []));
        } catch (error) {
            console.error('Failed to load dependencies:', error);
            toast.error('Failed to load form lookup data');
        } finally {
            setIsLoadingData(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // Filter reservations by selected room to help trace easily
    const filteredReservations = formData.roomId 
        ? reservations.filter(r => 
            String(r.roomId) === String(formData.roomId) || 
            String(r.room?.id) === String(formData.roomId) ||
            (Array.isArray(r.rooms) && r.rooms.some(rm => 
                String(rm.roomId) === String(formData.roomId) || 
                String(rm.id) === String(formData.roomId) ||
                String(rm.room?.id) === String(formData.roomId)
            ))
          )
        : [];

    const handleSubmit = async () => {
        // Validation
        if (!formData.description || !formData.roomId || !formData.dateFound || !formData.foundByStaffId) {
            toast.error("Please fill all required fields");
            return;
        }

        setIsSubmitting(true);
        try {
            // Strict Schema Clean-up Enforcement
            const payload = {
                description: formData.description,
                roomId: formData.roomId,
                dateFound: formData.dateFound, // Already in YYYY-MM-DD from input[type="date"]
                foundByStaffId: formData.foundByStaffId,
                storageLocation: formData.storageLocation?.trim() || undefined,
                reservationId: formData.reservationId || undefined // optional
            };

            await HousekeepingService.logLostFoundItem(payload, tenantId);
            
            toast.success("Found item logged securely into the system registry");
            
            if (onSuccess) onSuccess();
            onClose();
        } catch (error) {
            console.error("Submission error:", error);
            toast.error(error.message || "Failed to log found item");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal open={open} onClose={isSubmitting ? undefined : onClose}>
            <Box sx={{
                position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                width: { xs: '90%', sm: 550 }, bgcolor: 'background.paper', borderRadius: 3, p: 4, boxShadow: 24,
            }}>
                <IconButton sx={{ position: 'absolute', top: 12, right: 12 }} onClick={onClose} disabled={isSubmitting}>
                    <CloseIcon />
                </IconButton>

                <Typography variant="h6" fontWeight="bold" mb={3}>
                    Log Guest Item Found
                </Typography>

                {isLoadingData ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <TextField 
                                fullWidth 
                                label="Item Description *" 
                                name="description" 
                                value={formData.description} 
                                onChange={handleChange} 
                                size="small"
                                placeholder="Silver Rolex Wristwatch"
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Autocomplete
                                options={rooms}
                                getOptionLabel={(option) => option?.roomNumber ? `${option.roomNumber} (${option.type || option.roomType?.name || 'Room'})` : (option?.name || option?.number || '')}
                                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                value={rooms.find(r => r.id === formData.roomId) || null}
                                onChange={(event, newValue) => {
                                    setFormData(prev => ({ 
                                        ...prev, 
                                        roomId: newValue ? newValue.id : '',
                                        reservationId: '' // reset reservation if room changes
                                    }));
                                }}
                                renderInput={(params) => (
                                    <TextField {...params} label="Room Found *" size="small" />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField 
                                fullWidth 
                                label="Date Found *" 
                                type="date"
                                name="dateFound" 
                                value={formData.dateFound} 
                                onChange={handleChange} 
                                size="small"
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Autocomplete
                                options={staff}
                                getOptionLabel={(option) => option.fullName || option.name || `${option.firstName || ''} ${option.lastName || ''}`.trim() || ''}
                                value={staff.find(s => s.id === formData.foundByStaffId) || null}
                                onChange={(event, newValue) => {
                                    setFormData(prev => ({ ...prev, foundByStaffId: newValue ? newValue.id : '' }));
                                }}
                                renderInput={(params) => (
                                    <TextField {...params} label="Discovered By *" size="small" />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField 
                                fullWidth 
                                label="Storage Location" 
                                name="storageLocation" 
                                value={formData.storageLocation} 
                                onChange={handleChange} 
                                size="small"
                                placeholder="Vault-B, Shelf-2"
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Autocomplete
                                options={filteredReservations}
                                disabled={!formData.roomId}
                                getOptionLabel={(option) => {
                                    if (!option) return '';
                                    const guestName = option.guestName || option.guest?.name || 'Guest';
                                    const ref = option.bookingId || option.bookingReference || '';
                                    return ref ? `${guestName} (${ref})` : guestName;
                                }}
                                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                value={reservations.find(r => r.id === formData.reservationId) || null}
                                onChange={(event, newValue) => {
                                    setFormData(prev => ({ 
                                        ...prev, 
                                        reservationId: newValue ? newValue.id : ''
                                    }));
                                }}
                                renderInput={(params) => (
                                    <TextField 
                                        {...params} 
                                        label="Associated Booking (Traceability)" 
                                        size="small" 
                                        placeholder={formData.roomId ? "Select reservation if known..." : "Select room first to choose booking"} 
                                        helperText={!formData.roomId ? "Select a room first to enable booking selection" : undefined}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>
                )}

                <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
                    <Button fullWidth variant="outlined" onClick={onClose} disabled={isSubmitting || isLoadingData}>Cancel</Button>
                    <Button 
                        fullWidth 
                        variant="contained" 
                        onClick={handleSubmit} 
                        disabled={isSubmitting || isLoadingData}
                        startIcon={isSubmitting && <CircularProgress size={16} color="inherit" />}
                    >
                        {isSubmitting ? 'Logging...' : 'Log Found Item'}
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
}
